import { z } from 'zod';
import { v1ActorSchema } from './actor';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
/**
 * Public Audit Logs resource for `/api/v1` (KIR-4376).
 *
 * Read-only by design: the log is a record of what happened, not a resource a
 * customer can edit. There is no write endpoint and there will not be one in
 * `v1`.
 */
/**
 * What happened to the object.
 *
 * `REVOKED` is a first-class operation rather than an `UPDATED` with a
 * particular diff: revoking a credential is an `UPDATE` at the table level, and
 * a SIEM rule that has to reconstruct "a key was revoked" from a jsonb diff is
 * a rule that breaks the first time the column layout changes.
 */
export const v1AuditOperationSchema = z
    .enum(['CREATED', 'UPDATED', 'DELETED', 'REVOKED', 'LOGIN', 'LOGOUT'])
    .meta({
    id: 'V1AuditOperation',
    title: 'Audit operation',
    description: 'What happened to the object. `REVOKED` is first-class rather than an `UPDATED` with a particular diff, so a rule that wants "a credential was revoked" does not have to reconstruct it from a field list.',
});
/**
 * One audit event.
 *
 * `objectType` / `objectId` are the public names for the internal
 * `tableName` / `recordId`: a customer should not have to know that Kirin's
 * audit trail is table-shaped, and renaming the underlying columns must not be
 * a breaking change for them.
 *
 * `changedFields` is the **names** of the fields that changed — never their
 * values. `beforeData` / `afterData` are not exposed at all.
 *
 * Both of those are the same decision, and it is a security boundary rather than
 * a matter of taste. The audit trigger records changes as
 * `{ column: { from: <old value>, to: <new value> } }` over every audited table,
 * and some of those columns hold live credentials — `slack_settings.webhook_url`
 * is a bearer URL that lets its holder post into the customer's channel. Row
 * snapshots and per-field diffs therefore carry exactly the same class of
 * secret, so dropping one while publishing the other would have protected
 * nothing. `objectType=slack_settings` makes that directly queryable, which is
 * what turns it from incidental exposure into a lookup.
 *
 * Names alone are also what the stated use actually needs: a SIEM rule keys off
 * *which* fields moved, and a customer who needs the values has the UI, where
 * the reader is a person the organization has already vetted.
 */
export const v1AuditEventSchema = z
    .object({
    id: z.string().describe('Unique id for this event.'),
    timestamp: z
        .string()
        .datetime()
        .describe('When it happened (ISO 8601 UTC).'),
    organizationId: z
        .string()
        .nullable()
        .describe('The organization the event belongs to.'),
    objectType: z
        .string()
        .describe('What kind of thing changed, e.g. `organization_members` or `slack_settings`. These are the values the `objectType` filter accepts.'),
    operation: v1AuditOperationSchema,
    /** Id of the affected record. Null for events with no single subject (login). */
    objectId: z
        .string()
        .nullable()
        .describe('Id of the affected record. Null for events with no single subject, such as a login.'),
    /**
     * Who did it. When a management API key made the change, `actor.type` is
     * `apiKey`, `actor.userId` is the key's creator, and `actor.apiKey` names the
     * key — the KIR-3747 attribution model, stated in one place.
     */
    actor: v1ActorSchema.describe('Who did it.'),
    /**
     * Names of the fields this event changed, sorted. Empty for an event that
     * touched no field; null when the event records no field-level diff at all
     * (a login, say). Values are deliberately absent — see the doc above.
     */
    changedFields: z
        .array(z.string())
        .nullable()
        .describe('Names of the fields this event changed, sorted. Empty when the event touched no field; null when it records no field-level diff at all, such as a login. Values are deliberately never published — some audited columns hold live credentials, so the names are what a detection rule keys off and the values stay in the app.'),
})
    .meta({
    id: 'V1AuditEvent',
    title: 'Audit event',
    description: 'One recorded change in your organization: what changed, who changed it, and when. `objectType` and `objectId` are the public names for the underlying table and row, so the storage layout can change without breaking you.',
});
/** Sortable fields. Only timestamp today; the tie-break on event id makes it total. */
export const v1AuditEventSortFieldSchema = z
    .enum(['timestamp'])
    .describe('Field to sort by. Made total by an internal tie-break on the event id, so a walk across pages sees each event exactly once.');
/**
 * `GET /api/v1/organizations/:organizationId/audit-logs` query.
 *
 * Filters per the ticket: object type, operation, user, date range.
 */
export const v1AuditEventListQuerySchema = v1PaginationQuerySchema.extend({
    /** Object types, as returned in `objectType`. */
    objectType: csvParam('Keep only events on these object types, as returned in `objectType`.'),
    /** Operations, as returned in `operation`. */
    operation: csvParam('Keep only events with these operations.'),
    /** Kirin user ids, as returned in `actor.userId`. */
    userId: csvParam('Keep only events by these Kirin user ids, as returned in `actor.userId`.'),
    /** Exclusive lower bound: events strictly after this instant. */
    after: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only events strictly after this instant (ISO 8601). Exclusive, so paging a window by feeding the last result back in never re-reads the boundary row.'),
    /** Exclusive upper bound: events strictly before this instant. */
    before: z
        .string()
        .datetime()
        .optional()
        .describe('Keep only events strictly before this instant (ISO 8601).'),
    sortBy: v1AuditEventSortFieldSchema.default('timestamp'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1AuditEventListResponseSchema = z
    .object({
    data: z.array(v1AuditEventSchema).describe('This page of audit events.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1AuditEventList',
    title: 'Audit event list',
    description: 'A page of audit events, plus where it sits in the full result set.',
});
//# sourceMappingURL=audit-logs.js.map